import {
  Appearance,
  DraftConfiguration,
  DraftConfigurationWithText,
  PrintArea,
  PrintType,
  StoreFont,
} from '@/types';
import { assertNever } from '@/utils/assertNever';
import { round } from '@/utils/math/round';
import { getTextSizeForStyle } from '@/utils/text/getTextSizeForStyle';
import { scaleSize } from '@/model/configuration/scaleSize';
import { printTypesForDesign } from '@/model/printType/printTypesForDesign';
import { printTypesForSpecialText } from '@/model/printType/printTypesForSpecialText';
import { printTypesForText } from '@/model/printType/printTypesForText';
import { getIsDieCutStickerAppearance } from '@/model/productType/getIsDieCutStickerAppearance';
import {
  isDraftBendingTextConfiguration,
  isDraftConfigurationWithText,
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from './isDraftConfigurationType';

const getMinimumTextSize = (configuration: DraftConfigurationWithText, font: StoreFont) => {
  const text = configuration.text;

  if (isDraftBendingTextConfiguration(configuration)) {
    // minimum scale for bent text must only have two digits because the designer api will create a design with this value as minimum scale and minimum scales are of type int in our system
    const minimumScale = round(font.minimalSize / 25, 2);

    return scaleSize(configuration.size, minimumScale);
  }

  return getTextSizeForStyle({ ...text.style, fontSize: font.minimalSize }, text.content);
};

export const getPossiblePrintTypes = (
  configuration: DraftConfiguration,
  printArea: PrintArea,
  appearance: Appearance
): PrintType[] => {
  if (isDraftDesignConfiguration(configuration)) {
    return printTypesForDesign(configuration.design, printArea, appearance, configuration.rotation);
  }

  // only allow design configurations on the die cut sticker
  if (getIsDieCutStickerAppearance(appearance)) {
    return [];
  }

  if (isDraftConfigurationWithText(configuration)) {
    const font = configuration.text.style.font;
    return printTypesForText(
      printArea,
      appearance,
      font,
      getMinimumTextSize(configuration, font),
      font.minimalSize,
      configuration.rotation
    );
  }

  if (isDraftSpecialTextConfiguration(configuration)) {
    return printTypesForSpecialText(printArea, appearance);
  }

  return assertNever(configuration);
};
